import React, { useEffect, useState, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchEmployees,
  createEmployee,
  updateEmployee,
  deleteEmployee,
  changePasswordBySuperadmin,
  clearError,
  exportEmployees,
  importConfirm,
  importPreview,
} from "../../store/slices/employeeSlice";
import { fetchDepartments } from "../../store/slices/departmentSlice";
import {
  fetchEscalationHistory,
  clearError as clearEscHistoryError,
} from "../../store/slices/escalationHistorySlice";
import { fetchRoles } from "../../store/slices/roleSlice";
import {
  fetchWalletHistory,
  creditWallet,
  debitWallet,
} from "../../store/slices/walletSlice";

// Escalation Redux Thunks (pure Redux, no RTK Query)
import {
  fetchEscalations,
  createEscalation,
  updateEscalation,
  deleteEscalation,
  bulkCreateEscalations,
  setSearchTerm,
  setCurrentPage,
  setPageSize,
} from "../../store/slices/escalationSlice";
import { fetchCompanyDropdown, fetchDepartmentDropdown, fetchEmployeeDropdown, fetchRoleDropdown, restoreRole } from 'store/slices/commonSlice';
import {
  Pencil,
  Trash2,
  MoreHorizontal,
  Loader2,
  Eye,
  EyeOff,
  X,
  Lock,
  Key,
  Phone,
  MapPin,
  User,
  Wallet,
  History,
  Plus,
  ArrowUpDown,
  AlertCircle,
  MoreVertical,
  ChevronRight,
} from "lucide-react";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../../components/ui/popover";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "../../components/ui/command";
import { ChevronsUpDown, Check } from "lucide-react";
import DataTable from "../../components/common/DataTable";
import PageHeader from "../../components/common/PageHeader";
import FormField from "../../components/common/FormField";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../../components/ui/tabs";
import { Input } from "../../components/ui/input";
import { Label } from "components/ui/label";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { usePermissions } from "hooks/usePermissions";
import usePermissionSocket from "hooks/usePermissionSocket";
import { DialogDescription } from "@radix-ui/react-dialog";
import { fetchQuestions } from "store/slices/securityQuestionSlice";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import ReactCountryFlag from "react-country-flag";
import { getCountries, getCountryCallingCode } from "libphonenumber-js";
import { cn } from "lib/utils";
import { fetchCompanies } from "store/slices/companySlice";
import { formatDateTime } from "utlis/formateDateTime";
import { isSuperAdmin } from "utlis/authUtils";

// === ADD THIS CUSTOM MULTI-SELECT COMPONENT NEAR THE TOP (after imports) ===
// Reusable MultiSelect for Escalate To
const MultiSelectEscalateTo = ({ value = [], onChange, employees = [], currentEmployeeId }) => {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  // === DEFENSIVE: Always ensure value is an array ===
  const safeValue = Array.isArray(value) ? value : (value ? [value] : []);

  const filtered = employees.filter(emp =>
    emp.id !== currentEmployeeId &&
    (emp?.name + " " + emp?.code)
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const selected = safeValue
    .map(id => employees.find(e => e.id === id))
    .filter(Boolean);

  const toggle = (id) => {
    const current = safeValue;
    if (current.includes(id)) {
      onChange(current.filter(v => v !== id));
    } else {
      onChange([...current, id]);
    }
  };

  const remove = (id) => {
    onChange(safeValue.filter(v => v !== id));
  };

  return (
    <div className="space-y-2">
      <Label>Escalate To (Multiple)</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-full justify-between h-auto py-2"
          >
            <div className="flex flex-wrap gap-1 max-w-full">
              {selected.length === 0 ? (
                <span className="text-muted-foreground">Select managers...</span>
              ) : (
                selected.map(emp => (
                  <Badge key={emp.id} variant="secondary" className="mr-1 mb-1">
                    {emp?.name} ({emp?.code})
                  </Badge>
                ))
              )}
            </div>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0" align="start">
          <Command onWheel={(e) => e.stopPropagation()}>
            <CommandInput
              placeholder="Search employee..."
              value={search}
              onValueChange={setSearch}
            />
            <CommandList>
              <CommandEmpty>No employee found.</CommandEmpty>
              <CommandGroup>
                {filtered.map(emp => {
                  const isSelected = safeValue.includes(emp.id);
                  return (
                    <CommandItem
                      key={emp.id}
                      onSelect={() => toggle(emp.id)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4",
                          isSelected ? "opacity-100" : "opacity-0"
                        )}
                      />
                      {emp?.name} ({emp?.code})
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Selected Tags with Remove */}
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {selected.map(emp => (
            <Badge
              key={emp.id}
              variant="secondary"
              className="cursor-pointer hover:bg-secondary/80"
              onClick={() => remove(emp.id)}
            >
              {emp.name}
              <X className="ml-1 h-3 w-3" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};

const EmployeePage = () => {
  usePermissionSocket();
  const dispatch = useDispatch();
  const superAdmin = isSuperAdmin();
  // Employee State
  const {
    employees,
    pagination,
    isLoading: empLoading,
    error,
  } = useSelector((state) => state.employee);
  const employee = useSelector((state) => state.common.employees.items);

  console.log("employee", employee);
  // const { departments } = useSelector((state) => state.department);
  // const { companies } = useSelector((state) => state.company);
  const escalationHistoryState = useSelector((state) => state.escalationHistory);
  const {
    list: escalationHistory,
    pagination: escHistPagination,
    isLoading: escHistLoading,
    error: escHistError,
  } = escalationHistoryState;
  // console.log("companies", companies);
  // const { roles } = useSelector((state) => state.role);
  const { history } = useSelector((state) => state.wallet);
  // Escalation State (from Redux slice)
  const escalationState = useSelector((state) => state.escalation);
  const {
    list: escalations,
    pagination: escPagination,
    isLoading: escLoading,
    isBulkCreating,
    searchTerm: escSearch,
    currentPage: escPage,
    pageSize: escPageSize,
  } = escalationState;
  // Permissions
  const { canCreate, canUpdate, canDelete, canExport, canRead, canImport } =
    usePermissions("employee");
  const { canCreate: canCreditWallet, canUpdate: canUpdateWallet, canRead: canReadWallet, canDelete: canDeleteWallet } = usePermissions("employee_wallet");
  const { canRead: canReadWalletHistory } = usePermissions("employee_wallet_history");
  const { canCreate: canCreateEsc, canRead: canReadEsc, canUpdate: canUpdateEsc, canDelete: canDeleteEsc } = usePermissions("escalation");
  const { canRead: canReadEscHistory } = usePermissions("escalation_history");
  // Tabs
  const [activeTab, setActiveTab] = useState("employees");
  // Employee Dialog States
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isCreditDialogOpen, setIsCreditDialogOpen] = useState(false);
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [creditForm, setCreditForm] = useState({
    amount: "",
    error: "",
    isSubmitting: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  // New: Update Escalations List Dialog
  const [updateEscDialogOpen, setUpdateEscDialogOpen] = useState(false);
  const [currentUpdateEmployee, setCurrentUpdateEmployee] = useState(null);
  const [employeeEscalations, setEmployeeEscalations] = useState([]);
  const [editingEscalation, setEditingEscalation] = useState(null); // currently editing row
  const [updateForm, setUpdateForm] = useState({
    tat: "",
    escalate_to: "",
  });
  const [isAddingNew, setIsAddingNew] = useState(false); // for adding a new rule in update dialog
  const [newRuleForm, setNewRuleForm] = useState({
    tat: "00:30:00",
    escalate_to: "",
  });

  const [isEscDeleteDialogOpen, setIsEscDeleteDialogOpen] = useState(false);
  const [currentEscalationToDelete, setCurrentEscalationToDelete] = useState(null);
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);
  const [selectedEmployeeDetails, setSelectedEmployeeDetails] = useState(null);
  const [isWalletDialogOpen, setIsWalletDialogOpen] = useState(false);
  const [walletAction, setWalletAction] = useState("credit"); // 'credit' | 'debit'
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [isEscHistoryDialogOpen, setIsEscHistoryDialogOpen] = useState(false);
  const [selectedEmployeeForHistory, setSelectedEmployeeForHistory] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ADD THESE 3 LINES:
  const [escHistorySearch, setEscHistorySearch] = useState("");
  const [escHistoryPage, setEscHistoryPage] = useState(1);
  const [escHistoryPageSize, setEscHistoryPageSize] = useState(10);
  const openEscalationHistoryModal = async (emp) => {
    setSelectedEmployeeForHistory(emp);
    setIsEscHistoryDialogOpen(true);
    // fetch history for this employee
    dispatch(
      fetchEscalationHistory({
        escalatedFrom: emp.id,
        page: 1,
        pageSize: 10,
      })
    );
  };
  const openWalletModal = (emp, action = "credit") => {
    if (action === "credit" && !canCreditWallet) {
      return toast.error("No permission to credit wallet");
    }
    if (action === "debit" && !canCreditWallet) {
      return toast.error("No permission to debit wallet");
    }
    setCurrentEmployee(emp);
    setWalletAction(action);
    setCreditForm({ amount: "", error: "", isSubmitting: false }); // reuse same form
    setIsWalletDialogOpen(true);
  };
  // Form validation errors
  const [formErrors, setFormErrors] = useState({
    first_name: "",
    code: "",
    department: "",
    role: "",
  });
  // Add this new state for validation errors
  const [escFormErrors, setEscFormErrors] = useState({
    order: "",
    tat: "",
    employee: "",
    escalate_to: "",
  });
  // Update your existing escForm state to include isSubmitting
  const [escForm, setEscForm] = useState({
    order: 1,
    tat: "00:30:00",
    employee: "",
    escalate_to: [],
    isSubmitting: false, // Add this line
  });
  // Employee Form
  const [formData, setFormData] = useState({
    code: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    department: "",
    role: "",
    company: "",
    contact_no: "",
    address: "",
    allocate_manager: "",
    start_allocate_break_time: "",
    end_allocate_break_time: "",
    shift_closing_time: "",
    lunch_duration: "00:30:00",
    // is_issuance: false,
    is_active: true,
    profile_img: null,
    can_manage_issuance_it: false,
    can_manage_issuance_non_it: false,
    can_manage_ticket_it: false,
    can_manage_ticket_non_it: false,
    can_manage_orders: false,
    shift_start_time: "",
    // can_view_everything: false,
    can_manage_everything: false,
    can_reject_issuance: false,
    dob: "",
    doj: "",
    doe: "",
  });
  const [passwordForm, setPasswordForm] = useState({
    newPassword: "",
    show: false,
    isSubmitting: false,
  });
  const [passwordError, setPasswordError] = useState("");
  // Pagination & Search - Employees
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [escalationData, setEscalationData] = useState([]);
  const [nextOrder, setNextOrder] = useState(1)
  // Escalation Dialog
  const [escDialogOpen, setEscDialogOpen] = useState(false);
  const [currentEscalation, setCurrentEscalation] = useState(null);
  const [isBulkMode, setIsBulkMode] = useState(true); // New state for bulk mode
  const [bulkEscForms, setBulkEscForms] = useState([ // New state for bulk forms
    {
      order: 1,
      tat: "00:30:00",
      employee: "",
      escalate_to: [],
    }
  ]);
  // const [escForm, setEscForm] = useState({
  // order: 1,
  // tat: "00:30:00",
  // employee: "",
  // escalate_to: "",
  // });
  // Fetch security questions on mount
  const { questions = [] } = useSelector((state) => state.securityQuestion);
  // Re-fetch escalation history when modal opens or filters change
  useEffect(() => {
    if (selectedEmployeeForHistory && isEscHistoryDialogOpen) {
      dispatch(
        fetchEscalationHistory({
          escalatedFrom: selectedEmployeeForHistory.id,
          page: 1,
          pageSize: 10000,
        })
      );
    }
  }, [escHistorySearch, escHistoryPage, escHistoryPageSize, selectedEmployeeForHistory, isEscHistoryDialogOpen, dispatch]);
  useEffect(() => {
    dispatch(fetchQuestions());
  }, [dispatch]);
  // Fetch Employees
  const refetchEmployees = useCallback(() => {
    dispatch(fetchEmployees({ page, pageSize, search: searchTerm }));
  }, [dispatch, page, pageSize, searchTerm]);
  // Fetch Escalations on mount and when filters change
  useEffect((emp) => {
    dispatch(
      fetchEscalations({
        page: escPage,
        pageSize: escPageSize,
        search: escSearch,
        employee: emp?.id,
      })
    );
  }, [dispatch, escPage, escPageSize, escSearch]);
  useEffect(() => {
    refetchEmployees();
    // dispatch(fetchDepartments({ page: 1, pageSize: 1000, is_active: true }));
    // dispatch(fetchRoles({ page: 1, pageSize: 1000, is_active: true }));
    // dispatch(fetchCompanies({ page: 1, pageSize: 1000, is_active: true }));
    dispatch(fetchEmployeeDropdown({}));
    dispatch(fetchCompanyDropdown());
    dispatch(fetchDepartmentDropdown());
    dispatch(fetchRoleDropdown());
  }, [dispatch, refetchEmployees]);
  useEffect(() => {
    if (error) {
      toast.error(
        typeof error === "string" ? error : error.detail ?? "An error occurred"
      );
      dispatch(clearError());
    }
  }, [error, dispatch]);
  // Add this useEffect — forces DataTable to respect searchValue
  useEffect(() => {
    // This tricks DataTable into using the external search value
    // by resetting its internal state when escSearch changes
    const input = document.querySelector('[placeholder="Search escalation rules..."]');
    if (input && input.value !== escSearch) {
      input.value = escSearch;
    }
  }, [escSearch]);

  const openEscalationDialog = async (esc = null, employee = null) => {
    const employeeId = employee?.id || null;
    let fetchedEscalations = [];
    if (employeeId) {
      try {
        const response = await dispatch(
          fetchEscalations({
            page: 1,
            pageSize: 1000,
            employee: employeeId,
          })
        ).unwrap();
        fetchedEscalations = response.data || response || [];
        setEscalationData(fetchedEscalations); // Still update state for display
      } catch (err) {
        toast.error("Failed to load existing escalations");
        console.error(err);
        fetchedEscalations = [];
        setEscalationData([]);
      }
    }
    if (esc) {
      // Edit mode
      setIsBulkMode(false);
      setCurrentEscalation(esc);
      setEscForm({
        order: esc.order,
        tat: esc.tat,
        employee: esc.employee,
        // escalate_to: esc.escalate_to,
        escalate_to: Array.isArray(esc.escalate_to) ? esc.escalate_to : [esc.escalate_to].filter(Boolean),
      });
      setNextOrder(esc.order);
    } else {
      // Add mode
      setIsBulkMode(true);
      setCurrentEscalation(null);
      // Use fetchedEscalations (fresh data) for calculation
      let calculatedNextOrder = 1;
      if (fetchedEscalations.length > 0) {
        const maxOrder = Math.max(...fetchedEscalations.map(e => e.order));
        calculatedNextOrder = maxOrder + 1; // → 3 (from your data: orders 1,1,2)
      }
      setNextOrder(calculatedNextOrder);
      const initialForm = {
        order: calculatedNextOrder,
        tat: "00:30:00",
        employee: employeeId || "",
        escalate_to: "",
      };
      setBulkEscForms([initialForm]);
      // if (fetchedEscalations.length > 0) {
      //   toast.info(`Employee has ${fetchedEscalations.length} rule(s). Next level: ${calculatedNextOrder}`);
      // }
    }
    setEscDialogOpen(true);
  };
  // New function to open bulk escalation dialog
  const openBulkEscalationDialog = () => {
    setIsBulkMode(true);
    setBulkEscForms([{
      order: 1,
      tat: "00:30:00",
      employee: "",
      escalate_to: "",
    }]);
    setEscDialogOpen(true);
  };
  // Function to add a new escalation form
  const addEscalationForm = () => {
    const lastOrder = bulkEscForms.length > 0
      ? Math.max(...bulkEscForms.map(f => f.order))
      : 0;
    setBulkEscForms([
      ...bulkEscForms,
      {
        order: lastOrder + 1,
        tat: "00:30:00",
        employee: bulkEscForms[0]?.employee || "", // Use the same employee as the first form
        escalate_to: [],
      }
    ]);
  };
  // Function to remove an escalation form
  const removeEscalationForm = (index) => {
    if (bulkEscForms.length > 1) {
      const newForms = [...bulkEscForms];
      newForms.splice(index, 1);
      setBulkEscForms(newForms);
    }
  };
  // Function to update a specific escalation form
  const updateBulkEscForm = (index, field, value) => {
    const newForms = [...bulkEscForms];
    newForms[index] = {
      ...newForms[index],
      [field]: value
    };
    setBulkEscForms(newForms);
  };

  //   const validateEscalationForm = () => {
  //   const errors = {
  //     tat: !escForm.tat ? "TAT is required" : "",
  //     employee: !escForm.employee ? "From Employee is required" : "",
  //     escalate_to: !escForm.escalate_to ? "Escalate To is required" : "",
  //   };
  //   setEscFormErrors(errors);
  //   return Object.values(errors).every(error => error === "");
  // };
  // Function to validate all bulk escalation forms
  // const validateBulkEscalationForms = () => {
  //   const errors = bulkEscForms.map(form => ({
  //     order: !form.order || form.order <= 0 ? "Level must be a positive number" : "",
  //     tat: !form.tat ? "TAT is required" : "",
  //     employee: !form.employee ? "From Employee is required" : "",
  //     escalate_to: !form.escalate_to ? "Escalate To is required" : "",
  //   }));
  //   setEscFormErrors(errors[0]); // Just use the first error for simplicity
  //   return errors.every(error => Object.values(error).every(err => err === ""));
  // };


  const handleValidationError = (error) => {

    // Handle the new bulk API error format (array of objects)
    if (Array.isArray(error)) {
      let allErrors = [];

      // Collect all non_field_errors from each object in the array
      error.forEach(errorObj => {
        if (errorObj.non_field_errors && Array.isArray(errorObj.non_field_errors)) {
          allErrors = [...allErrors, ...errorObj.non_field_errors];
        }
      });

      // Remove duplicates and display all errors
      // const uniqueErrors = [...new Set(allErrors)];
      const uniqueErrors = [...new Set(allErrors.map(msg => msg.replace(/_/g, ' ')))];
      if (uniqueErrors.length > 0) {
        // Join all errors with newlines for better readability
        toast.error(uniqueErrors.join('\n'));
      }
      return;
    }

    // Handle the original error format (object with field-specific errors)
    if (typeof error === 'object' && error !== null) {
      // Handle field-specific errors
      let errorMessage = "";

      if (error.code) {
        errorMessage += error.code.join(", ");
      }

      if (error.email) {
        errorMessage += (errorMessage ? " " : "") + error.email.join(", ");
      }

      // if (error.non_field_errors) {
      //   errorMessage += (errorMessage ? " " : "") + error.non_field_errors.join(", ");
      // }

      if (error.non_field_errors) {
        errorMessage += (errorMessage ? " " : "") + error.non_field_errors.join(", ");
      }

      // if (error.detail) {
      //   errorMessage += (errorMessage ? " " : "") + error.detail;
      // }
      if (error.errors && Array.isArray(error.errors)) {
        errorMessage += error.errors.join("\n");
      } else if (error.detail) {
        errorMessage += (errorMessage ? " " : "") + error.detail;
      }

      // If we couldn't extract specific errors, show a generic message
      if (!errorMessage) {
        errorMessage = "Validation error. Please check your input.";
      }

      toast.error(errorMessage);

      // Update form errors for specific fields
      if (error.code) {
        setFormErrors(prev => ({ ...prev, code: error.code.join(", ") }));
      }

      if (error.email) {
        // We don't have email in formErrors, but we could add it if needed
        // setFormErrors(prev => ({ ...prev, email: error.email.join(", ") }));
      }
    } else {
      // Handle string errors
      toast.error(error || "An error occurred");
    }
  };

  const validateBulkEscalationForms = () => {
    const errors = bulkEscForms.map(form => ({
      order: !form.order || form.order <= 0 ? "Level must be positive" : "",
      tat: !form.tat ? "TAT is required" : "",
      employee: !form.employee ? "From Employee is required" : "",
      escalate_to: form.escalate_to.length === 0 ? "Select at least one manager" : "",
    }));
    setEscFormErrors(errors[0] || {});
    return errors.every(e => Object.values(e).every(v => v === ""));
  };

  const validateEscalationForm = () => {
    const errors = {
      tat: !escForm.tat ? "TAT is required" : "",
      employee: !escForm.employee ? "From Employee is required" : "",
      escalate_to: escForm.escalate_to.length === 0 ? "Select at least one manager" : "",
    };
    setEscFormErrors(errors);
    return Object.values(errors).every(v => v === "");
  };

  const handleEscalationSubmit = async () => {
    if (isBulkMode) {
      // Handle bulk submission
      if (!validateBulkEscalationForms()) {
        return; // Stop if validation fails
      }
      // Set local submitting state to true
      setIsSubmitting(true);
      try {
        await dispatch(bulkCreateEscalations(bulkEscForms)).unwrap();
        toast.success(`${bulkEscForms.length} escalation rules created`);
        setEscDialogOpen(false);
      } catch (err) {
        console.log("err", err);
        handleValidationError(err);
      } finally {
        // IMPORTANT: Always reset the local submitting state
        setIsSubmitting(false);
      }
    } else {
      // Handle single submission
      if (!validateEscalationForm()) {
        return; // Stop if validation fails
      }
      // Set local submitting state to true
      setIsSubmitting(true);
      try {
        if (currentEscalation) {
          await dispatch(
            updateEscalation({ id: currentEscalation.id, ...escForm })
          ).unwrap();
          toast.success("Escalation updated");
        } else {
          await dispatch(createEscalation(escForm)).unwrap();
          toast.success("Escalation created");
        }
        setEscDialogOpen(false);
      } catch (err) {
        console.log("err", err);
        handleValidationError(err);
      } finally {
        // IMPORTANT: Always reset the local submitting state
        setIsSubmitting(false);
      }
    }
  };

  //   const openUpdateEscalations = async (emp) => {
  //   setCurrentUpdateEmployee(emp);
  //   try {
  //     const response = await dispatch(
  //       fetchEscalations({
  //         page: 1,
  //         pageSize: 1000,
  //         employee: emp.id,
  //       })
  //     ).unwrap();
  //     const data = response.data || response || [];
  //     console.log("upadte data", data)
  //     setEmployeeEscalations(data);
  //   } catch (err) {
  //     toast.error("Failed to load escalations");
  //     setEmployeeEscalations([]);
  //   }
  //   setUpdateEscDialogOpen(true);
  // };

  const openUpdateEscalations = async (emp) => {
    setCurrentUpdateEmployee(emp);
    setEmployeeEscalations([]); // Reset
    setEditingEscalation(null);
    setIsAddingNew(false);

    try {
      const response = await dispatch(
        fetchEscalations({
          page: 1,
          pageSize: 1000,
          employee: emp.id,
        })
      ).unwrap();

      const fetchedData = response?.data || response || [];
      // Sort by order ascending
      const sortedData = [...fetchedData].sort((a, b) => (a.order || 0) - (b.order || 0));
      setEmployeeEscalations(sortedData);
    } catch (err) {
      console.error("Failed to load escalations:", err);
      toast.error("Failed to load escalation rules");
      setEmployeeEscalations([]);
    }
    setUpdateEscDialogOpen(true);
  };

  // === START EDITING A SINGLE RULE ===
  const startEdit = (esc) => {
    setEditingEscalation(esc);
    setUpdateForm({
      tat: esc.tat || "00:30:00",
      escalate_to: esc.escalate_to || "",
    });
  };

  // === CANCEL EDITING ===
  const cancelEdit = () => {
    setEditingEscalation(null);
    setUpdateForm({ tat: "", escalate_to: "" });
  };

  // === SAVE UPDATED RULE ===
  const saveEdit = async () => {
    if (!updateForm.tat || !updateForm.escalate_to) {
      toast.error("Please fill TAT and Escalate To");
      return;
    }

    try {
      await dispatch(
        updateEscalation({
          id: editingEscalation.id,
          tat: updateForm.tat,
          escalate_to: updateForm.escalate_to,
        })
      ).unwrap();

      toast.success("Escalation rule updated");
      // Refresh list
      openUpdateEscalations(currentUpdateEmployee);
    } catch (err) {
      console.log("Update escalation error:", err);

      if (err?.non_field_errors?.length > 0) {
        toast.error(err.non_field_errors[0]);
      } else if (err?.detail) {
        toast.error(err.detail);
      } else {
        toast.error("Failed to update rule");
      }
    }
  };

  // === DELETE SINGLE RULE (re-use existing confirm dialog) ===
  const handleDeleteEscalation = (escalation) => {
    setCurrentEscalationToDelete(escalation);
    setIsEscDeleteDialogOpen(true);
  };

  // === ADD NEW RULE IN UPDATE DIALOG ===
  const addNewRule = async () => {
    if (!newRuleForm.tat || !newRuleForm.escalate_to) {
      toast.error("Please fill TAT and Escalate To");
      return;
    }

    const nextOrder = employeeEscalations.length > 0
      ? Math.max(...employeeEscalations.map(e => e.order)) + 1
      : 1;

    try {
      await dispatch(
        createEscalation({
          order: nextOrder,
          tat: newRuleForm.tat,
          employee: currentUpdateEmployee.id,
          escalate_to: newRuleForm.escalate_to,
        })
      ).unwrap();

      toast.success("New escalation rule added");
      setIsAddingNew(false);
      setNewRuleForm({ tat: "00:30:00", escalate_to: "" });
      // Refresh
      openUpdateEscalations(currentUpdateEmployee);
    } catch (err) {
      toast.error("Failed to add rule");
    }
  };


  // const handleDeleteEscalation = (escalation) => {
  //   setCurrentEscalationToDelete(escalation);
  //   setIsEscDeleteDialogOpen(true);
  // };
  // const confirmDeleteEscalation = async () => {
  //   if (!currentEscalationToDelete) return;
  //   try {
  //     await dispatch(deleteEscalation(currentEscalationToDelete.id)).unwrap();
  //     toast.success("Escalation rule deleted");
  //     setIsEscDeleteDialogOpen(false);
  //     setCurrentEscalationToDelete(null);
  //   } catch {
  //     toast.error("Failed to delete escalation rule");
  //   }
  // };

  const confirmDeleteEscalation = async () => {
    if (!currentEscalationToDelete) return;

    try {
      await dispatch(deleteEscalation(currentEscalationToDelete.id)).unwrap();

      toast.success("Escalation rule deleted");

      // ✅ close delete confirmation dialog
      setIsEscDeleteDialogOpen(false);
      setCurrentEscalationToDelete(null);

      // ✅ ALSO close update escalation dialog
      setUpdateEscDialogOpen(false);

      // optional: reset edit state
      setEditingEscalation(null);

    } catch {
      toast.error("Failed to delete escalation rule");
    }
  };

  // Employee Actions
  const openCreditModal = (emp) => {
    if (!canCreditWallet) return toast.error("No permission");
    setCurrentEmployee(emp);
    setCreditForm("");
    setIsCreditDialogOpen(true);
  };
  const openHistoryModal = async (emp) => {
    setCurrentEmployee(emp);
    await dispatch(fetchWalletHistory({ walletId: emp.wallet_id }));
    setIsHistoryDialogOpen(true);
  };
  const handleCreditAmountChange = (e) => {
    const value = e.target.value;
    const numValue = Number(value);
    let error = "";
    if (value && (isNaN(numValue) || numValue <= 0)) {
      error = "Invalid amount";
    } else if (numValue > 1000) {
      error = "Maximum amount is ₹1000";
    }
    setCreditForm((prev) => ({
      ...prev,
      amount: value,
      error: error,
    }));
  };

  const handleCreditSubmit = async () => {
    const amount = Number(creditForm.amount);
    if (!amount || amount <= 0 || creditForm.error) return;
    if (walletAction === "debit" && amount > (currentEmployee?.wallet_balance || 0)) {
      return toast.error("Cannot debit more than available balance");
    }
    try {
      setCreditForm(prev => ({ ...prev, isSubmitting: true }));
      if (walletAction === "credit") {
        await dispatch(creditWallet({ walletId: currentEmployee.wallet_id, amount })).unwrap();
        toast.success(`₹${amount} credited successfully!`);
      } else {
        await dispatch(debitWallet({ walletId: currentEmployee.wallet_id, amount })).unwrap();
        toast.success(`₹${amount} debited successfully!`);
      }
      setIsWalletDialogOpen(false);
      refetchEmployees(); // refresh balance
    } catch (err) {
      console.log("err", err);
      toast.error(err?.amount?.[0] || "Transaction failed");
    } finally {
      setCreditForm(prev => ({ ...prev, isSubmitting: false }));
    }
  };
  const openPasswordModal = (emp) => {
    setCurrentEmployee(emp);
    setPasswordForm({ newPassword: "", show: false });
    setIsPasswordDialogOpen(true);
  };
  const handlePasswordChange = async () => {
    if (passwordForm.newPassword.length < 6)
      return setPasswordError("Min 6 characters");
    try {
      // Set submitting state to true
      setPasswordForm(prev => ({ ...prev, isSubmitting: true }));
      await dispatch(
        changePasswordBySuperadmin({
          id: currentEmployee.id,
          newPassword: passwordForm.newPassword,
        })
      ).unwrap();
      toast.success("Password changed");
      setIsPasswordDialogOpen(false);
    } catch {
      toast.error("Failed");
    } finally {
      // Reset submitting state
      setPasswordForm(prev => ({ ...prev, isSubmitting: false }));
    }
  };
  const validateForm = () => {
    const errors = {
      first_name: formData.first_name ? "" : "First name is required",
      code: formData.code ? "" : "Employee code is required",
      department: formData.department ? "" : "Department is required",
      role: formData.role ? "" : "Role is required",
      // email: formData.email ? "" : "Email is required",
      // password: formData.password ? "" : "Password is required",
      email: formData.email.trim()
        ? /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
          ? ""
          : "Enter a valid email address"
        : "Email is required",
      password: !currentEmployee
        ? formData.password
          ? formData.password.length >= 6
            ? ""
            : "Password must be at least 6 characters"
          : "Password is required"
        : "",
    };
    setFormErrors(errors);
    // Return true if no errors
    return Object.values(errors).every(error => error === "");
  };
  const handleOpenDialog = (employee = null) => {
    if (employee && !canUpdate) return toast.error("No permission");
    if (!employee && !canCreate) return toast.error("No permission");
    // Reset form errors
    setFormErrors({
      first_name: "",
      code: "",
      department: "",
      role: "",
    });
    if (employee) {
      setCurrentEmployee(employee);
      setFormData({
        code: employee.code || "",
        email: employee.email || "",
        first_name: employee.first_name || "",
        last_name: employee.last_name || "",
        password: "",
        department: employee.department || "",
        role: employee.role || "",
        company: employee.company || "",
        contact_no: employee.contact_no || "",
        address: employee.address || "",
        allocate_manager: employee.allocate_manager || "",
        start_allocate_break_time: employee.start_allocate_break_time || "",
        end_allocate_break_time: employee.end_allocate_break_time || "",
        shift_closing_time: employee.shift_closing_time || "",
        lunch_duration: employee.lunch_duration || "00:30:00",
        // is_issuance: employee.is_issuance || false,
        is_active: employee.is_active || "",
        profile_img: null,
        can_manage_issuance_it: employee.can_manage_issuance_it || false,
        can_manage_issuance_non_it:
          employee.can_manage_issuance_non_it || false,
        can_manage_ticket_it: employee.can_manage_ticket_it || false,
        can_manage_ticket_non_it: employee.can_manage_ticket_non_it || false,
        can_manage_orders: employee.can_manage_orders || false,
        shift_start_time: employee.shift_start_time || "",
        // can_view_everything: employee.can_view_everything || false,
        can_manage_everything: employee.can_manage_everything || false,
        can_reject_issuance: employee.can_reject_issuance || false,
        dob: employee.dob || "",
        doj: employee.doj || "",
        doe: employee.doe || "",
      });
      setPreviewUrl(employee.profile_img || null);
    } else {
      setCurrentEmployee(null);
      setFormData({
        code: "",
        email: "",
        first_name: "",
        last_name: "",
        password: "",
        department: "",
        role: "",
        company: "",
        contact_no: "",
        address: "",
        allocate_manager: "",
        start_allocate_break_time: "",
        end_allocate_break_time: "",
        shift_closing_time: "",
        lunch_duration: "00:30:00",
        // is_issuance: false,
        is_active: true,
        profile_img: null,
        can_manage_issuance_it: false,
        can_manage_issuance_non_it: false,
        can_manage_ticket_it: false,
        can_manage_ticket_non_it: false,
        can_manage_orders: false,
        shift_start_time: "",
        // can_view_everything: false,
        can_manage_everything: false,
        can_reject_issuance: false,
        dob: "",
        doj: "",
        doe: "",
      });
      setPreviewUrl(null);
    }
    setShowPassword(false);
    setIsDialogOpen(true);
  };
 const handleSubmit = async (e) => {
  e.preventDefault();
  
  if (!validateForm()) {
    toast.error("Please fill in all required fields");
    return;
  }

  const data = new FormData();
  Object.keys(formData).forEach((key) => {
    // Only skip null, but allow empty strings for optional fields
    if (formData[key] !== null && formData[key] !== undefined) {
      // For time fields, send empty string if cleared
      if (formData[key] === "" && 
          !["start_allocate_break_time", "end_allocate_break_time", 
            "shift_closing_time", "shift_start_time", "lunch_duration"].includes(key)) {
        return; // Skip empty non-time fields
      }
      data.append(key, formData[key]);
    }
  });

  if (!currentEmployee && formData.password)
    data.append("password", formData.password);

  try {
    if (currentEmployee) {
      await dispatch(
        updateEmployee({ id: currentEmployee.id, formData: data })
      ).unwrap();
      toast.success("Employee updated");
    } else {
      await dispatch(createEmployee(data)).unwrap();
      toast.success("Employee created");
    }
    setIsDialogOpen(false);
    refetchEmployees();
  } catch (err) {
    handleValidationError(err);
  }
};

const handleTimeChange = (e) => {
  const { name, value } = e.target;
  setFormData((prev) => ({
    ...prev,
    [name]: value,
  }));
};

const handleClearTime = (fieldName) => {
  setFormData((prev) => ({
    ...prev,
    [fieldName]: "",
  }));
};

  const handleDelete = async () => {
    try {
      await dispatch(deleteEmployee(currentEmployee.id)).unwrap();
      toast.success("Employee deleted");
      setIsDeleteDialogOpen(false);
      refetchEmployees();
    } catch {
      toast.error("Failed to delete");
    }
  };
  // Generic handler for most form fields
  const handleChange = (e) => {
    const { name, value, files, checked, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : files ? files[0] : value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
    if (name === "profile_img" && files?.[0]) {
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(files[0]);
    }
  };
  // Specialized handlers for name fields (alphabetic characters only)
  const handleNameChange = (e) => {
    const { name, value } = e.target;
    // Allow only alphabetic characters and spaces
    const nameValue = value.replace(/[^a-zA-Z\s]/g, '');
    setFormData((prev) => ({
      ...prev,
      [name]: nameValue,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ""
      }));
    }
  };
  // Specialized handler for contact number (numeric characters only)
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    // Allow only numbers
    const contactValue = value.replace(/[^0-9]/g, '');
    setFormData((prev) => ({
      ...prev,
      [name]: contactValue,
    }));
  };
  const removeImage = () => {
    setPreviewUrl(null);
    setFormData((prev) => ({ ...prev, profile_img: null }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  /* -------------------------- IMPORT CALLBACKS -------------------------- */
  const handleImportPreview = async (file) => {
    if (!canImport) throw new Error("You don't have import permission");
    const result = await dispatch(importPreview(file)).unwrap();
    return result;
  };
  const handleImportConfirm = async (preview) => {
    if (!canImport) throw new Error("You don't have import permission");
    await dispatch(importConfirm(preview)).unwrap();
  };

  const {
    items: departments = [],
    loading: departmentLoading,
  } = useSelector((state) => state.common.departments);

  const {
    items: companies = [],
    loading: companyLoading,
  } = useSelector((state) => state.common.companies);

  const {
    items: roles = [],
    loading: roleLoading,
  } = useSelector((state) => state.common.roles);

  const columns = [
    {
      accessorKey: "code",
      header: "Employee Code",
      cell: ({ row }) => {
        const emp = row.original;
        return (
          <Tooltip delayDuration={0}>
            <TooltipTrigger asChild>
              <button
                onClick={() => {
                  setSelectedEmployeeDetails(emp);
                  setIsDetailsDialogOpen(true);
                }}
                className="inline-flex items-center gap-2 font-semibold text-blue-600 transition-all duration-200 group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded-md px-1 -mx-1"
              >
                <span className="group-hover:underline">{emp?.code}</span>
                <Eye className="h-3.5 w-3.5 text-blue-400 group-hover:text-blue-600 group-hover:scale-110 transition-all duration-200" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="font-medium">View profile</p>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "full_name",
      header: "Employee Name",
      cell: ({ row }) => (
        <div>
          <p className="font-medium">
            {row.original.first_name} {row.original.last_name}
          </p>
          <p className="text-xs text-muted-foreground">{row.original.email}</p>
        </div>
      ),
    },
    {
      accessorKey: "company_name", header: "Company Name", cell: ({ row }) =>
        row.original.company_name || "-",
    },
    {
      accessorKey: "department_name", header: "Department", cell: ({ row }) =>
        row.original.department_name || "-",
    },
    // { accessorKey: "role_name", header: "Role" },
    {
      accessorKey: "allocate_manager_name",
      header: "Reporting Manager",
      cell: ({ row }) => row.original.allocate_manager_name || "-",
    },
    {
      accessorKey: "doj",
      header: "Date of Joining",
      cell: ({ row }) => row.original.doj || "-",
    },
    // {
    // accessorKey: "lunch_duration",
    // header: "Lunch Duration",
    // cell: ({ row }) => row.original.lunch_duration || "-",
    // },
    // {
    // accessorKey: "start_allocate_break_time",
    // header: "Break Start",
    // cell: ({ row }) =>
    // row.original.start_allocate_break_time?.slice(0, 5) || "-",
    // },
    // {
    // accessorKey: "end_allocate_break_time",
    // header: "Break End",
    // cell: ({ row }) =>
    // row.original.end_allocate_break_time?.slice(0, 5) || "-",
    // },
    // {
    // accessorKey: "wallet_balance",
    // header: "Wallet Balance",
    // cell: ({ row }) => row.original.wallet_balance || "-",
    // },
    {
      accessorKey: "is_active",
      header: "Status",
      cell: ({ row }) => (
        <Badge variant={row.original.is_active ? "success" : "secondary"}>
          {row.original.is_active ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created On',
      cell: ({ row }) => <span className="text-sm">{formatDateTime(row.original.created_at) ?? '-'}</span>,
    },
    {
      accessorKey: 'created_by_name',
      header: 'Created By',
      cell: ({ row }) => <span className="text-sm">{row.original.created_by_name ?? '-'}</span>,
    },
    {
      id: "actions",
      header: 'Actions',
      cell: ({ row }) => {
        const emp = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {canUpdate && (
                <DropdownMenuItem onClick={() => handleOpenDialog(emp)}>
                  <Pencil className="mr-2 h-4 w-4" /> Edit
                </DropdownMenuItem>
              )}
              {canUpdate && (
                <DropdownMenuItem onClick={() => openPasswordModal(emp)}>
                  <Key className="mr-2 h-4 w-4" /> Reset Password
                </DropdownMenuItem>
              )}
              {canUpdate && canReadWallet && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <Wallet className="mr-2 h-4 w-4" />
                    Wallet
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {canCreditWallet &&
                    <DropdownMenuItem onClick={() => openWalletModal(emp, "credit")}>
                      <Wallet className="mr-2 h-4 w-4" />
                      Credit Wallet
                    </DropdownMenuItem>
                    }
                    {canUpdateWallet &&
                    <DropdownMenuItem onClick={() => openWalletModal(emp, "debit")}>
                      <Wallet className="mr-2 h-4 w-4 text-red-600" />
                      Debit Wallet
                    </DropdownMenuItem>
                      }
                      {canReadWalletHistory &&
                    <DropdownMenuItem onClick={() => openHistoryModal(emp)}>
                      <History className="mr-2 h-4 w-4" />
                      Wallet History
                    </DropdownMenuItem>
                    }
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {/*
              {canCreditWallet && canUpdate && (
                <DropdownMenuItem onClick={() => openWalletModal(emp, "credit")}>
                  <Wallet className="mr-2 h-4 w-4" /> Credit Wallet
                </DropdownMenuItem>
              )}
              {canCreditWallet && canUpdate && (
                <DropdownMenuItem onClick={() => openWalletModal(emp, "debit")}>
                  <Wallet className="mr-2 h-4 w-4 text-red-600" /> Debit Wallet
                </DropdownMenuItem>
              )} */}
              {/* {canUpdate && (
                <>
                  <DropdownMenuItem onClick={() => openEscalationDialog(null, emp)}>
                    <ArrowUpDown className="mr-2 h-4 w-4" /> Add Escalation
                  </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => openEscalationDialog(null, emp)}>
                    <ArrowUpDown className="mr-2 h-4 w-4" /> Update Escalation
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => openEscalationHistoryModal(emp)}>
                    <History className="mr-2 h-4 w-4" /> Escalation History
                  </DropdownMenuItem>
                </>
              )} */}
              {canUpdate && canReadEsc && (
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <ArrowUpDown className="mr-2 h-4 w-4" />
                    Escalation
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    {canCreateEsc &&
                    <DropdownMenuItem onClick={() => openEscalationDialog(null, emp)}>
                      <ArrowUpDown className="mr-2 h-4 w-4" />
                      Add Escalation
                    </DropdownMenuItem>
                   }
                    {canUpdateEsc &&
                    <DropdownMenuItem onClick={() => openUpdateEscalations(emp)}>
                      <ArrowUpDown className="mr-2 h-4 w-4" />
                      Update Escalation
                    </DropdownMenuItem>
                    }
                   {canReadEscHistory &&
                    <DropdownMenuItem onClick={() => openEscalationHistoryModal(emp)}>
                      <History className="mr-2 h-4 w-4" />
                      Escalation History
                    </DropdownMenuItem>
                    }
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
              )}
              {canDelete && (
                <DropdownMenuItem
                  onClick={() => {
                    setCurrentEmployee(emp);
                    setIsDeleteDialogOpen(true);
                  }}
                  className="text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" /> Delete
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];
  return (
    <div className="space-y-6 animate-fade-in">
      {(() => {
        // const { departments = [], loading: departmentLoading } = useSelector((state) => state.common.department);




        // Show loading if either is loading
        if (departmentLoading || companyLoading || roleLoading) {
          return (
            <>  </>


          );
        }
        const noRoles = roles.length === 0;
        const noCompanies = companies.length === 0;
        const noDepartments = departments.length === 0;
        // Show both warnings if both are empty
        if (noCompanies || noDepartments || noRoles) {
          return (
            <div className="mt-6 space-y-4">
              {noRoles && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700 font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    No roles configured
                  </p>
                  <p className="text-xs text-red-600 mt-1">
                    Employees cannot be created until at least one role is added (e.g., Admin, Employee, Manager).
                  </p>
                </div>
              )}
              {noDepartments && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-700 font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    No departments configured
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    You can still create employees, but they won't be assigned to any department.
                  </p>
                </div>
              )}
              {noCompanies && (
                <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
                  <p className="text-sm text-orange-700 font-medium flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    No companies configured
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    You can still create employees, but they won't be assigned to any company.
                  </p>
                </div>
              )}
            </div>
          );
        }
        // All good → show nothing
        return null;
      })()}
      <PageHeader
        title="Employees"
        description="Manage employees and escalation rules"
        // action={
        // canCreate
        // ? {
        // label: "Add Employee", onClick: () => handleOpenDialog(), disabled: (questions?.length ?? 0) === 0,
        // className:
        // (questions?.length ?? 0) === 0
        // ? "opacity-50 cursor-not-allowed"
        // : "",
        // }
        // : null
        // }
        action={
          canCreate
            ? {
              label: "Add Employee",
              onClick: () => handleOpenDialog(),
              // Disable if ANY of these are missing
              disabled:
                (roles?.length ?? 0) === 0 ||
                (departments?.length ?? 0) === 0 ||
                (companies?.length ?? 0) === 0,
              // Optional: Visual feedback when disabled
              className: cn(
                "transition-all",
                (
                  (roles?.length ?? 0) === 0 ||
                  (departments?.length ?? 0) === 0 || companies?.length === 0)
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:shadow-lg"
              ),
            }
            : null
        }
      />
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        {/* <TabsList className="grid w-full max-w-md mx-auto grid-cols-2">
          <TabsTrigger value="employees">
            Employees
          </TabsTrigger>
          <TabsTrigger value="escalations">
            Escalations
          </TabsTrigger>
        </TabsList> */}
        {/* Employees Tab */}
        <TabsContent value="employees" className="space-y-6">
          {canRead ? (
            <DataTable
              data={employees}
              totalCount={pagination.count}
              columns={columns}
              templateUrl="/import-template/employees.csv"
              templateDownloadName="employees-import-template.csv"
              templateKey="employee"
              searchPlaceholder="Search employees..."
              // onExport={() => dispatch(exportEmployees())}
              onExport={canExport ? () => dispatch(exportEmployees()) : undefined}
              showExport={canExport}
              onImportPreview={canImport ? handleImportPreview : undefined}
              onImportConfirm={canImport ? handleImportConfirm : undefined}
              pageIndex={page - 1}
              pageSize={pageSize}
              pageCount={Math.ceil(pagination.count / pagination.page_size)}
              onPageChange={(p) => setPage(p + 1)}
              onPageSizeChange={setPageSize}
              onSearch={setSearchTerm}
              isLoading={empLoading}
              onDelete={async (id) => {
                await dispatch(deleteEmployee(id));
                refetchEmployees();
              }}
              onRestore={async (id) => {
                await dispatch(restoreRole({ entityType: 'employees', id }));
                refetchEmployees();
              }}
            />
          ) : (
            <p className="text-center py-12 text-muted-foreground">
              No permission to view employees
            </p>
          )}
        </TabsContent>

      </Tabs>

      {/* add escalation */}
      <Dialog open={escDialogOpen} onOpenChange={setEscDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isBulkMode
                ? "Create Multiple Escalation Rules"
                : (currentEscalation ? "Edit" : "Add") + " Escalation Rule"}
            </DialogTitle>
          </DialogHeader>
          {isBulkMode ? (
            // Bulk escalation forms
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <p className="text-sm text-muted-foreground">
                  Create multiple escalation rules at once. Each rule represents an escalation level.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={addEscalationForm}
                  className="flex items-center gap-1"
                >
                  <Plus className="h-4 w-4" /> Add Rule
                </Button>
              </div>
              {/* {escalationData.length > 0 && (
  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
    <p className="font-medium text-amber-800 mb-2">
      Existing Escalation Rules ({escalationData.length})
    </p>
    <div className="space-y-2 text-sm">
      {[...escalationData] // ← Create shallow copy
        .sort((a, b) => a.order - b.order)
        .map((e) => (
          <div key={e.id} className="flex justify-between">
            <span>Level {e.order} → {e.escalate_to_name}</span>
            <span className="text-muted-foreground">TAT: {e.tat}</span>
          </div>
        ))}
    </div>
    <p className="text-xs text-amber-700 mt-2">
      New rule will be added as Level {nextOrder}
    </p>
  </div>
)} */}
              {escalationData.length > 0 && (
                <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-medium text-amber-800 mb-3">
                    Existing Escalation Rules ({escalationData.length})
                  </p>

                  <div className="overflow-x-auto">
                    <table className="w-full border border-amber-200 rounded-md text-sm">
                      <thead className="bg-amber-100">
                        <tr>
                          <th className="px-3 py-2 text-left font-medium text-amber-900 border-b">
                            Order
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-amber-900 border-b">
                            Escalated To
                          </th>
                          <th className="px-3 py-2 text-left font-medium text-amber-900 border-b">
                            TAT
                          </th>
                        </tr>
                      </thead>

                      <tbody className="bg-white">
                        {[...escalationData]
                          .sort((a, b) => a.order - b.order)
                          .map((e) => (
                            <tr key={e.id} className="border-b last:border-b-0">
                              <td className="px-3 py-2">
                                Level {e.order}
                              </td>
                              <td className="px-3 py-2">
                                {e.escalate_to_name}
                              </td>
                              <td className="px-3 py-2 text-muted-foreground">
                                {e.tat}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                  <p className="text-xs text-amber-700 mt-3">
                    New rule will be added as <strong>Level {nextOrder}</strong>
                  </p>
                </div>
              )}

              {bulkEscForms.map((form, index) => (
                <div key={index} className="border rounded-lg p-4 space-y-4">
                  <div className="flex justify-between items-center">
                    {/* <h3 className="font-medium">Escalation Rule #{index + 1}</h3> */}
                    {bulkEscForms.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeEscalationForm(index)}
                        className="text-destructive hover:bg-destructive/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Order Number</Label>
                      {/* <FormField
                        min="1"
                        type="number"
                        value={form.order}
                        onChange={(e) => updateBulkEscForm(index, 'order', Number(e.target.value))}
                        className={escFormErrors.order ? "border-red-500" : ""}
                      /> */}
                      <FormField
                        min="1"
                        type="number"
                        value={form.order}
                        disabled={true} // ← Disable
                        className="bg-muted cursor-not-allowed"
                      />
                      {escFormErrors.order && index === 0 && (
                        <p className="text-sm text-red-500 mt-1">{escFormErrors.order}</p>
                      )}
                    </div>
                    <div>
                      <Label>TAT (hh:mm:ss)</Label>
                      <FormField
                        type="time"
                        step="1"
                        value={form.tat}
                        onChange={(e) => updateBulkEscForm(index, 'tat', e.target.value)}
                        className={escFormErrors.tat ? "border-red-500" : ""}
                      />
                      {escFormErrors.tat && index === 0 && (
                        <p className="text-sm text-red-500 mt-1">{escFormErrors.tat}</p>
                      )}
                    </div>
                    <div>
                      <Label>From Employee</Label>
                      {(() => {
                        // Find the selected employee from the list using the ID stored in the form
                        const selected = employees.find((e) => e.id === form.employee);
                        return (
                          <div
                            className={`mt-2 w-full px-3 py-2 border rounded-md bg-gray-50 ${escFormErrors.employee ? "border-red-500" : ""}`}
                          >
                            {selected ? (
                              `${selected.first_name} ${selected.last_name} (${selected.code})`
                            ) : (
                              <span className="text-gray-400">Select Employee</span>
                            )}
                          </div>
                        );
                      })()}
                      {escFormErrors.employee && index === 0 && (
                        <p className="text-sm text-red-500 mt-1">{escFormErrors.employee}</p>
                      )}
                    </div>
                    {/* <div>
                      <Label>Escalate To</Label>
                      <select
                        className={`w-full px-3 py-2 border rounded-md ${escFormErrors.escalate_to ? "border-red-500" : ""}`}
                        value={form.escalate_to}
                        onChange={(e) => updateBulkEscForm(index, 'escalate_to', e.target.value)}
                      >
                        <option value="">Select Manager</option>
                        {employees.map((e) => (
                          <option key={e.id} value={e.id}>
                            {e.first_name} {e.last_name} ({e.code})
                          </option>
                        ))}
                      </select>
                      {escFormErrors.escalate_to && index === 0 && (
                        <p className="text-sm text-red-500 mt-1">{escFormErrors.escalate_to}</p>
                      )}
                    </div> */}
                    {/* <FormField
                      id={`escalate_to_${index}`}
                      label="Escalate To"
                      name="escalate_to"
                      type="select"
                      value={form.escalate_to}
                      onChange={(e) => updateBulkEscForm(index, "escalate_to", e.target.value)}
                      required
                      options={[
                        { value: "", label: "Select Manager" },
                        ...employee.map((emp) => ({
                          value: emp.id,
                          label: emp.name,
                        })),
                      ]}
                      className={escFormErrors.escalate_to && index === 0 ? "border-red-500" : ""}
                    />
                    {escFormErrors.escalate_to && index === 0 && (
                      <p className="text-sm text-red-500 mt-1">{escFormErrors.escalate_to}</p>
                    )} */}
                    {/* Replace the old FormField for escalate_to with this */}
                    {/* <div className="md:col-span-2"> */}
                    <MultiSelectEscalateTo
                      value={form.escalate_to}
                      onChange={(newValue) => updateBulkEscForm(index, "escalate_to", newValue)}
                      employees={employee}
                      currentEmployeeId={form.employee}
                    />
                    {escFormErrors.escalate_to && index === 0 && (
                      <p className="text-sm text-red-500 mt-1">{escFormErrors.escalate_to}</p>
                    )}
                    {/* </div> */}
                  </div>
                </div>
              ))}
            </div>
          ) : (

            // Single escalation form
            <div className="space-y-4">
              <div>
                <Label>Order Number</Label>
                <FormField
                  min="1"
                  type="number"
                  value={escForm.order}
                  onChange={(e) =>
                    setEscForm({
                      ...escForm,
                      order: Number(e.target.value),
                    })
                  }
                  className={escFormErrors.order ? "border-red-500" : ""}
                />
                {escFormErrors.order && (
                  <p className="text-sm text-red-500 mt-1">{escFormErrors.order}</p>
                )}
              </div>
              <div>
                <Label>TAT (hh:mm:ss)</Label>
                <FormField
                  type="time"
                  step="1"
                  value={escForm.tat}
                  onChange={(e) =>
                    setEscForm({ ...escForm, tat: e.target.value })
                  }
                  className={escFormErrors.tat ? "border-red-500" : ""}
                />
                {escFormErrors.tat && (
                  <p className="text-sm text-red-500 mt-1">{escFormErrors.tat}</p>
                )}
              </div>
              <div>
                <Label>From Employee</Label>
                {(() => {
                  const selected = employees.find((e) => e.id === escForm.employee);
                  return (
                    <div
                      className={`w-full px-3 py-2 border rounded-md bg-gray-50 ${escFormErrors.employee ? "border-red-500" : ""
                        }`}
                    >
                      {selected ? (
                        `${selected.first_name} ${selected.last_name} (${selected.code})`
                      ) : (
                        <span className="text-gray-400">Select Employee</span>
                      )}
                    </div>
                  );
                })()}
                {escFormErrors.employee && (
                  <p className="text-sm text-red-500 mt-1">{escFormErrors.employee}</p>
                )}
              </div>
              <div>
                <Label>Escalate To</Label>
                <select
                  className={`w-full px-3 py-2 border rounded-md ${escFormErrors.escalate_to ? "border-red-500" : ""}`}
                  value={escForm.escalate_to}
                  onChange={(e) =>
                    setEscForm({ ...escForm, escalate_to: e.target.value })
                  }
                >
                  <option value="">Select Manager</option>
                  {employees.map((e) => (
                    <option key={e.id} value={e.id}>
                      {e.first_name} {e.last_name} ({e.code})
                    </option>
                  ))}
                </select>
                {escFormErrors.escalate_to && (
                  <p className="text-sm text-red-500 mt-1">{escFormErrors.escalate_to}</p>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setEscDialogOpen(false)}
              disabled={escForm.isSubmitting || isBulkCreating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleEscalationSubmit}
            // disabled={isSubmitting || isBulkCreating} // Disable if local OR global submission is happening
            >
              {isBulkMode ? (
                isSubmitting ? ( // Use the local state for the loading text
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating Rules...
                  </>
                ) : (
                  `Create Rule`
                )
              ) : (
                isSubmitting ? ( // Use the local state for the loading text
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Rule"
                )
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ==================== NEW UPDATE ESCALATIONS DIALOG ==================== */}
      <Dialog open={updateEscDialogOpen} onOpenChange={setUpdateEscDialogOpen}>
        {/* <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto"> */}
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-visible">
          <DialogHeader>
            <DialogTitle>
              Update Escalation Rules – {currentUpdateEmployee?.first_name} {currentUpdateEmployee?.last_name} ({currentUpdateEmployee?.code})
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {employeeEscalations.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">
                No escalation rules defined yet.
              </p>
            ) : (
              <div className="space-y-4 max-h-[70vh] overflow-auto">
                <h4 className="font-medium">Existing Rules</h4>

                {employeeEscalations.map((esc) => (
                  <div
                    key={esc.id}
                    className="border rounded-lg p-4 bg-white"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">

                      {/* Level */}
                      <div>
                        <Label className="text-xs text-muted-foreground">Level</Label>
                        <p className="font-medium">{esc.order}</p>
                      </div>

                      {editingEscalation?.id === esc.id ? (
                        <>
                          {/* TAT */}
                          {/* <div>
                            <FormField
                              label="TAT"
                              type="time"
                              value={updateForm.tat}
                              onChange={(e) =>
                                setUpdateForm({ ...updateForm, tat: e.target.value })
                              }
                            />
                          </div> */}

                          <div>
                            {/* <Label className="text-xs text-muted-foreground">TAT</Label> */}
                            <FormField
                              label="TAT"
                              type="time"
                              step="1"
                              value={updateForm.tat}
                              onChange={(e) =>
                                setUpdateForm({ ...updateForm, tat: e.target.value })
                              }
                              className={escFormErrors?.tat ? "border-red-500" : ""}
                            />
                          </div>

                          {/* Escalate To */}
                          <div>
                            <FormField
                              label="Escalate To"
                              type="select"
                              value={updateForm.escalate_to}
                              onChange={(e) =>
                                setUpdateForm({ ...updateForm, escalate_to: e.target.value })
                              }
                              placeholder="Select Manager"
                              options={employee
                                .filter(e => e.id !== currentUpdateEmployee.id)
                                .map(e => ({
                                  value: e.id,
                                  label: `${e?.name} (${e?.code})`,
                                }))}
                            />
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 justify-end">
                            <Button size="sm" onClick={saveEdit}>
                              Save
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={cancelEdit}
                            >
                              Cancel
                            </Button>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* TAT */}
                          <div>
                            <Label className="text-xs text-muted-foreground">TAT</Label>
                            <p className="font-medium">{esc.tat}</p>
                          </div>

                          {/* Escalate To */}
                          <div>
                            <Label className="text-xs text-muted-foreground">Escalate To</Label>
                            <p className="font-medium">
                              {esc.escalate_to_name || esc.escalate_to}
                            </p>
                          </div>

                          {/* Actions */}
                          <div className="flex gap-2 justify-end">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEdit(esc)}
                            >
                              <Pencil className="h-4 w-4 mr-1" />
                              Edit
                            </Button>

                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeleteEscalation(esc)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}

              </div>
            )}

            {/* Add New Rule Section */}
            {/* <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-medium">Add New Escalation Level</h4>
                <Button
                  size="sm"
                  onClick={() => setIsAddingNew(true)}
                  disabled={isAddingNew}
                >
                  <Plus className="h-4 w-4 mr-1" /> Add Rule
                </Button>
              </div>

              {isAddingNew && (
                <div className="border rounded-lg p-4 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label>Level (auto)</Label>
                      <p className="font-medium">
                        {employeeEscalations.length > 0
                          ? Math.max(...employeeEscalations.map(e => e.order)) + 1
                          : 1}
                      </p>
                    </div>
                    <div>
                      <Label>TAT</Label>
                      <Input
                        type="time"
                        step="1"
                        value={newRuleForm.tat}
                        onChange={(e) => setNewRuleForm({ ...newRuleForm, tat: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Escalate To</Label>
                      <select
                        className="w-full px-3 py-2 border rounded-md"
                        value={newRuleForm.escalate_to}
                        onChange={(e) => setNewRuleForm({ ...newRuleForm, escalate_to: e.target.value })}
                      >
                        <option value="">Select Manager</option>
                        {employees
                          .filter(e => e.id !== currentUpdateEmployee?.id)
                          .map((e) => (
                            <option key={e.id} value={e.id}>
                              {e.first_name} {e.last_name} ({e.code})
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" size="sm" onClick={() => setIsAddingNew(false)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={addNewRule}>
                      Save New Rule
                    </Button>
                  </div>
                </div>
              )}
            </div> */}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setUpdateEscDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Universal Credit / Debit Wallet Dialog */}
      <Dialog open={isWalletDialogOpen} onOpenChange={setIsWalletDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="h-5 w-5" />
              {walletAction === "credit" ? "Credit" : "Debit"} Wallet
            </DialogTitle>
            <div className={`text-center py-4 rounded-lg border-2 border-dashed ${walletAction === "credit" ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"
              }`}>
              <p className="text-sm text-muted-foreground">Wallet Operation</p>
              <p className="text-xl font-bold">
                {currentEmployee?.first_name} {currentEmployee?.last_name}
              </p>
              <p className={`text-3xl font-bold mt-2 ${walletAction === "credit" ? "text-green-600" : "text-red-600"
                }`}>
                Current: ₹{currentEmployee?.wallet_balance || 0}
              </p>
            </div>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Amount (₹)</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={creditForm.amount}
                onChange={handleCreditAmountChange}
                placeholder="Enter amount"
                className={creditForm.error ? "border-red-500" : ""}
              />
              {creditForm.error && (
                <p className="text-sm text-destructive mt-1">{creditForm.error}</p>
              )}
              {walletAction === "debit" && Number(currentEmployee?.wallet_balance || 0) < Number(creditForm.amount || 0) && creditForm.amount && (
                <p className="text-sm text-destructive mt-1">Insufficient balance</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsWalletDialogOpen(false)}
              disabled={creditForm.isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant={walletAction === "credit" ? "default" : "destructive"}
              onClick={handleCreditSubmit}
              disabled={
                creditForm.isSubmitting ||
                !!creditForm.error ||
                !creditForm.amount ||
                (walletAction === "debit" && Number(currentEmployee?.wallet_balance || 0) < Number(creditForm.amount))
              }
            >
              {creditForm.isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : walletAction === "credit" ? (
                "Credit Amount"
              ) : (
                "Debit Amount"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Wallet History Dialog */}
      <Dialog open={isHistoryDialogOpen} onOpenChange={setIsHistoryDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              Wallet History - {currentEmployee?.first_name}{" "}
              {currentEmployee?.last_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {history && history[currentEmployee?.wallet_id] && history[currentEmployee?.wallet_id].items && history[currentEmployee?.wallet_id].items.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="px-4 py-3 text-left">Date</th>
                      <th className="px-4 py-3 text-left">Type</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                      <th className="px-4 py-3 text-right">Balance After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history[currentEmployee?.wallet_id].items.map((h) => (
                      <tr key={h.id} className="border-t">
                        <td className="px-4 py-3">
                          {new Date(h.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <Badge
                            variant={
                              h.transaction_type === "credit"
                                ? "default"
                                : "secondary"
                            }
                          >
                            {h.transaction_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          ₹{h.amount}
                        </td>
                        <td className="px-4 py-3 text-right">
                          ₹{h.balance_after}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center text-muted-foreground py-8">
                No transaction history
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* CREATE / EDIT DIALOG */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[95vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {currentEmployee ? "Edit Employee" : "Add New Employee"}
            </DialogTitle>
            <DialogDescription>
              Fill in the details below to{" "}
              {currentEmployee ? "update" : "create"} an employee.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Profile Image Section - Moved to top */}
            <div className="space-y-3">
              <Label>Profile Image</Label>
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-24 h-24 rounded-full border-2 border-dashed border-muted-foreground/50 flex items-center justify-center overflow-hidden bg-muted">
                    {previewUrl ? (
                      <img
                        src={previewUrl}
                        alt="Preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="h-10 w-10 text-muted-foreground" />
                    )}
                  </div>
                  {previewUrl && (
                    <button
                      type="button"
                      onClick={removeImage}
                      className="absolute -top-2 -right-2 bg-destructive text-white rounded-full p-1"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    name="profile_img"
                    onChange={handleChange}
                    className="hidden"
                    id="profile_img"
                  />
                  <label
                    htmlFor="profile_img"
                    className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 bg-secondary rounded-md text-sm"
                  >
                    Choose Image
                  </label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formData.profile_img?.name || "No file selected"}
                  </p>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className={formErrors.first_name ? "space-y-2" : ""}>
                {/* <Label htmlFor="first_name">First Name <span className="text-red-500">*</span></Label> */}
                <FormField
                  label="First Name"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleNameChange}
                  required
                  placeholder="John"
                  className={formErrors.first_name ? "border-red-500" : ""}
                />
                {formErrors.first_name && (
                  <p className="text-sm text-red-500">{formErrors.first_name}</p>
                )}
              </div>
              <FormField
                label="Last Name"
                name="last_name"
                value={formData.last_name}
                placeholder="John"
                onChange={handleNameChange}
              />
              <div className={formErrors.code ? "space-y-2" : ""}>
                <FormField
                  id="code"
                  label="Employee Code"
                  name="code"
                  value={formData.code}
                  onChange={handleChange}
                  required
                  placeholder="EMP001"
                  className={formErrors.code ? "border-red-500" : ""}
                />
                {formErrors.code && (
                  <p className="text-sm text-red-500">{formErrors.code}</p>
                )}
              </div>
              <div className={formErrors.email ? "space-y-2" : ""}>
                <FormField
                  label="Email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="abc@gmail.com"
                  className={formErrors.email ? "border-red-500" : ""}
                  required
                />
                {formErrors.email && (
                  <p className="text-sm text-red-500">{formErrors.email}</p>
                )}
              </div>
              {/* Date of Birth */}
              <FormField
                label="Date of Birth"
                name="dob"
                type="date"
                value={formData.dob || ""}
                onChange={handleChange}
                max={new Date().toISOString().split("T")[0]}
              />
              <FormField
                label="Contact Number"
                name="contact_no"
                value={formData.contact_no}
                onChange={handleContactChange}
                placeholder="1234567890"
                maxLength={10}
                icon={<Phone className="h-4 w-4" />}
              />
            </div>
            <FormField
              type="textarea"
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              placeholder="123 abc"
              icon={<MapPin className="h-4 w-4" />}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className={formErrors.department ? "space-y-2" : ""}>
                <FormField
                  id="department"
                  label="Department"
                  name="department"
                  type="select"
                  value={formData.department}
                  onChange={handleChange}
                  required
                  options={departments.map((d) => ({
                    value: d.id,
                    label: d.name,
                  }))}
                  className={formErrors.department ? "border-red-500" : ""}
                />
                {formErrors.department && (
                  <p className="text-sm text-red-500">{formErrors.department}</p>
                )}
              </div>
              <div className={formErrors.role ? "space-y-2" : ""}>
                <FormField
                  id="role"
                  label="Role"
                  name="role"
                  type="select"
                  value={formData.role}
                  onChange={handleChange}
                  required
                  options={roles.map((r) => ({ value: r.id, label: r.name }))}
                  className={formErrors.role ? "border-red-500" : ""}
                />
                {formErrors.role && (
                  <p className="text-sm text-red-500">{formErrors.role}</p>
                )}
              </div>
              <FormField
                id="company"
                label="Company"
                name="company"
                type="select"
                value={formData.company}
                options={companies.map((d) => ({
                  value: d.id,
                  label: d.name,
                }))}
                onChange={handleChange}
                placeholder="abc"
              />
              <FormField
                label="Allocate Manager"
                name="allocate_manager"
                type="select"
                value={formData.allocate_manager}
                onChange={handleChange}
                options={employee.map((e) => ({
                  value: e.id,
                  label: e.name,
                }))}
                placeholder="Select Manager"
              />
            </div>
            {!currentEmployee && (
              <div className={formErrors.password ? "space-y-2" : ""}>
                {/* <Label>Password</Label> */}
                <div className="relative">
                  <Lock className="absolute left-3 top-[45px] -translate-y-1/3 h-4 w-4 text-muted-foreground" />
                  <FormField
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="********"
                    className={formErrors.password ? "border-red-500 pl-10 pr-10" : "pl-10 pr-10"}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-[50px] -translate-y-1/2"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {formErrors.password && (
                  <p className="text-sm text-red-500">{formErrors.password}</p>
                )}
              </div>
            )}
            {/* New Section: Additional Employee Details */}
            <div className="space-y-6 border-t pt-6">
              <h3 className="text-lg font-medium">Additional Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <FormField
                  label="Break Start Time"
                  name="start_allocate_break_time"
                  type="time"
                  step="1"
                  value={formData.start_allocate_break_time}
                  onChange={handleChange}
                />
                <FormField
                  label="Break End Time"
                  name="end_allocate_break_time"
                  type="time"
                  step="1"
                  value={formData.end_allocate_break_time}
                  onChange={handleChange}
                />
                <FormField
                  label="Lunch/Dinner Duration"
                  name="lunch_duration"
                  type="time"
                  step="1"
                  value={formData.lunch_duration || "01:30:00"}
                  onChange={handleChange}
                />
                {/* Shift Start Time */}
                <FormField
                  label="Shift Start Time"
                  name="shift_start_time"
                  type="time"
                  step="1"
                  value={formData.shift_start_time || ""}
                  onChange={handleChange}
                />
                <FormField
                  label="Shift Closing Time"
                  name="shift_closing_time"
                  type="time"
                  step="1"
                  value={formData.shift_closing_time}
                  onChange={handleChange}
                />
                {/* Date of Joining */}
                <FormField
                  label="Date of Joining"
                  name="doj"
                  type="date"
                  value={formData.doj || ""}
                  onChange={handleChange}
                  required
                  max={new Date().toISOString().split("T")[0]}
                />
                {/* Date of Exit (Optional) */}
                <FormField
                  label="Date of Exit"
                  name="doe"
                  type="date"
                  value={formData.doe || ""}
                  onChange={handleChange}
                  max={new Date().toISOString().split("T")[0]}
                />
              </div>
            </div>
            {/* All Permission Toggles Grouped Together */}
            <div className="space-y-4 border-t pt-4">
              <h3 className="text-lg font-medium">Permissions</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                <FormField
                  label="IT Issuance"
                  name="can_manage_issuance_it"
                  type="switch"
                  checked={formData.can_manage_issuance_it}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({
                      ...p,
                      can_manage_issuance_it: c,
                      can_reject_issuance:
                        c || p.can_manage_issuance_non_it ? p.can_reject_issuance : false,
                    }))
                  }
                />
                <FormField
                  label="Non-IT Issuance"
                  name="can_manage_issuance_non_it"
                  type="switch"
                  checked={formData.can_manage_issuance_non_it}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({
                      ...p,
                      can_manage_issuance_non_it: c,
                      can_reject_issuance:
                        c || p.can_manage_issuance_it ? p.can_reject_issuance : false,
                    }))
                  }
                />
                <FormField
                  label="IT Ticket"
                  name="can_manage_ticket_it"
                  type="switch"
                  checked={formData.can_manage_ticket_it}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({ ...p, can_manage_ticket_it: c }))
                  }
                />
                <FormField
                  label="Non-IT Ticket"
                  name="can_manage_ticket_non_it"
                  type="switch"
                  checked={formData.can_manage_ticket_non_it}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({ ...p, can_manage_ticket_non_it: c }))
                  }
                />
                <FormField
                  label="Manage Orders"
                  name="can_manage_orders"
                  type="switch"
                  checked={formData.can_manage_orders}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({ ...p, can_manage_orders: c }))
                  }
                />
                {/* Super Permissions */}
                {/* <FormField
                  label="Can View Everything"
                  name="can_view_everything"
                  type="switch"
                  checked={formData.can_view_everything || false}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({ ...p, can_view_everything: c }))
                  }
                  description="Grants read access to all modules regardless of role"
                /> */}
                <FormField
                  label="Can Manage Everything"
                  name="can_manage_everything"
                  type="switch"
                  checked={formData.can_manage_everything || false}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({ ...p, can_manage_everything: c }))
                  }
                  description="Full admin access (use carefully)"
                />
                {superAdmin && (
                <FormField
                  label="Can Reject Issuance"
                  name="can_reject_issuance"
                  type="switch"
                  disabled={
                    !formData.can_manage_issuance_it &&
                    !formData.can_manage_issuance_non_it
                  }
                  checked={formData.can_reject_issuance || false}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({ ...p, can_reject_issuance: c }))
                  }
                />)}
              </div>
              <div className="flex items-center gap-6">
                <FormField
                  label="Active Status"
                  name="is_active"
                  type="switch"
                  checked={formData.is_active}
                  onCheckedChange={(c) =>
                    setFormData((p) => ({ ...p, is_active: c }))
                  }
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit">
                {currentEmployee ? "Update Employee" : "Create Employee"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
      {/* PASSWORD RESET DIALOG */}
      <Dialog
        open={isPasswordDialogOpen}
        onOpenChange={setIsPasswordDialogOpen}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set new password for {currentEmployee?.first_name}{" "}
              {currentEmployee?.last_name}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Input
                type={passwordForm.show ? "text" : "password"}
                value={passwordForm.newPassword}
                onChange={(e) => {
                  setPasswordForm((p) => ({
                    ...p,
                    newPassword: e.target.value,
                  }));
                  // Clear error when user starts typing
                  if (passwordError) {
                    setPasswordError("");
                  }
                }}
                placeholder="Enter new password"
                className={passwordError ? "border-red-500" : ""}
              />
              <button
                type="button"
                onClick={() =>
                  setPasswordForm((p) => ({ ...p, show: !p.show }))
                }
                className="absolute right-3 top-3"
              >
                {passwordForm.show ? (
                  <EyeOff className="h-4 w-4" />
                ) : (
                  <Eye className="h-4 w-4" />
                )}
              </button>
            </div>
            {passwordError && (
              <p className="text-sm text-destructive">{passwordError}</p>
            )}
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setIsPasswordDialogOpen(false)}
                disabled={passwordForm.isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePasswordChange}
                disabled={passwordForm.isSubmitting || !passwordForm.newPassword}
              >
                {passwordForm.isSubmitting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  "Change Password"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* DELETE CONFIRM */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. Employee "
              {currentEmployee?.first_name} {currentEmployee?.last_name}" will
              be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* ESCALATION DELETE CONFIRMATION */}
      <AlertDialog open={isEscDeleteDialogOpen} onOpenChange={setIsEscDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Escalation Rule?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The escalation rule for Level{" "}
              <strong>{currentEscalationToDelete?.order}</strong> will be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteEscalation}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete Rule
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {/* EMPLOYEE DETAILS MODAL */}
      <Dialog open={isDetailsDialogOpen} onOpenChange={setIsDetailsDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedEmployeeDetails && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold flex items-center gap-3">
                  <div className="relative">
                    {selectedEmployeeDetails.profile_img ? (
                      <img
                        src={selectedEmployeeDetails.profile_img}
                        alt={selectedEmployeeDetails.full_name}
                        className="w-16 h-16 rounded-full object-cover border-4 border-background shadow-lg"
                      />
                    ) : (
                      <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center">
                        <User className="h-8 w-8 text-muted-foreground" />
                      </div>
                    )}
                    {selectedEmployeeDetails.is_active ? (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-4 border-background"></div>
                    ) : (
                      <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-red-500 rounded-full border-4 border-background"></div>
                    )}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      {selectedEmployeeDetails.full_name}
                      {selectedEmployeeDetails.is_superuser && (
                        <Badge variant="default">Super Admin</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground font-medium">
                      {selectedEmployeeDetails.code}
                    </p>
                  </div>
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                {/* Personal Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">Personal Information</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Full Name</span>
                      <span className="font-medium">{selectedEmployeeDetails.full_name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Email</span>
                      <span className="font-medium">{selectedEmployeeDetails.email || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Contact</span>
                      <span className="font-medium">{selectedEmployeeDetails.contact_no || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Address</span>
                      <span className="font-medium">{selectedEmployeeDetails.address || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Company</span>
                      <span className="font-medium">{selectedEmployeeDetails.company_name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date of Birth</span>
                      <span className="font-medium">{selectedEmployeeDetails.dob || "-"}</span>
                    </div>
                  </div>
                </div>
                {/* Work Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">Work Information</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Department</span>
                      <span className="font-medium">{selectedEmployeeDetails.department_name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Role</span>
                      <span className="font-medium">{selectedEmployeeDetails.role_name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Role Level</span>
                      <Badge variant="outline">{selectedEmployeeDetails.role_level_display || "-"}</Badge>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date of Joining</span>
                      <span className="font-medium">{selectedEmployeeDetails.doj || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Date of Exit</span>
                      <span className="font-medium">{selectedEmployeeDetails.doe || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Manager</span>
                      <span className="font-medium">{selectedEmployeeDetails.allocate_manager_name || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Status</span>
                      <Badge variant={selectedEmployeeDetails.is_active ? "success" : "secondary"}>
                        {selectedEmployeeDetails.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </div>
                  </div>
                </div>
                {/* Wallet & Permissions */}
                {/* <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">Wallet & Permissions</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Wallet Balance</span>
                      <span className="font-bold text-lg text-green-600">
                        ₹{Number(selectedEmployeeDetails.wallet_balance || 0).toLocaleString()}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      {[
                        { label: "IT Issuance", value: selectedEmployeeDetails.can_manage_issuance_it },
                        { label: "Non-IT Issuance", value: selectedEmployeeDetails.can_manage_issuance_non_it },
                        { label: "IT Ticket", value: selectedEmployeeDetails.can_manage_ticket_it },
                        { label: "Non-IT Ticket", value: selectedEmployeeDetails.can_manage_ticket_non_it },
                        { label: "Manage Orders", value: selectedEmployeeDetails.can_manage_orders },
                      ].map((perm) => (
                        <div key={perm.label} className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${perm.value ? "bg-green-500" : "bg-gray-300"}`} />
                          <span className="text-xs">{perm.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div> */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">Wallet & Permissions</h3>
                  <div className="space-y-3 text-sm">
                    {/* Wallet Balance */}
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Wallet Balance</span>
                      <span className="font-bold text-lg text-green-600">
                        ₹{Number(selectedEmployeeDetails.wallet_balance || 0).toLocaleString()}
                      </span>
                    </div>
                    {/* Regular Permissions */}
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        { label: "IT Issuance", value: selectedEmployeeDetails.can_manage_issuance_it },
                        { label: "Non-IT Issuance", value: selectedEmployeeDetails.can_manage_issuance_non_it },
                        { label: "IT Ticket", value: selectedEmployeeDetails.can_manage_ticket_it },
                        { label: "Non-IT Ticket", value: selectedEmployeeDetails.can_manage_ticket_non_it },
                        { label: "Manage Orders", value: selectedEmployeeDetails.can_manage_orders },
                      ].map((perm) => (
                        <div key={perm.label} className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded ${perm.value ? "bg-green-500" : "bg-gray-300"}`} />
                          <span className="text-xs">{perm.label}</span>
                        </div>
                      ))}
                    </div>
                    {/* Super Permissions – Highlighted */}
                    <div className="pt-4 border-t">
                      <p className="text-xs font-medium text-muted-foreground mb-3">Super Permissions</p>
                      <div className="grid grid-cols-2 gap-4">
                        {/* <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded ${selectedEmployeeDetails.can_view_everything ? "bg-blue-600" : "bg-gray-300"}`} />
                          <span className="text-sm font-medium">
                            {selectedEmployeeDetails.can_view_everything ? "Can View Everything" : "View Everything"}
                          </span>
                        </div> */}
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-5 rounded ${selectedEmployeeDetails.can_manage_everything ? "bg-red-600" : "bg-gray-300"}`} />
                          <span className="text-sm font-medium">
                            {selectedEmployeeDetails.can_manage_everything ? "Can Manage Everything" : "Manage Everything"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Shift & Timing */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">Shift & Break Timing</h3>
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Lunch Duration</span>
                      <span className="font-medium">{selectedEmployeeDetails.lunch_duration || "-"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Break Start</span>
                      <span className="font-medium">
                        {selectedEmployeeDetails.start_allocate_break_time?.slice(0, 5) || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Break End</span>
                      <span className="font-medium">
                        {selectedEmployeeDetails.end_allocate_break_time?.slice(0, 5) || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Shift Start</span>
                      <span className="font-medium">
                        {selectedEmployeeDetails.shift_start_time?.slice(0, 5) || "-"}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Shift Closing</span>
                      <span className="font-medium">
                        {selectedEmployeeDetails.shift_closing_time?.slice(0, 5) || "-"}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Login & System Info */}
                <div className="md:col-span-2 space-y-4">
                  <h3 className="font-semibold text-lg border-b pb-2">System Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Last Login</span>
                      <p className="font-medium">
                        {selectedEmployeeDetails.last_login
                          ? new Date(selectedEmployeeDetails.last_login).toLocaleString()
                          : "-"}
                      </p>
                    </div>
                    {/* <div>
                <span className="text-muted-foreground">Last IP</span>
                <p className="font-medium">{selectedEmployeeDetails.last_login_ip || "-"}</p>
              </div> */}
                    <div>
                      <span className="text-muted-foreground">Joined On</span>
                      <p className="font-medium">
                        {new Date(selectedEmployeeDetails.date_joined).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter className="mt-6">
                <Button onClick={() => setIsDetailsDialogOpen(false)}>Close</Button>
                {canUpdate && (
                  <Button
                    onClick={() => {
                      handleOpenDialog(selectedEmployeeDetails);
                      setIsDetailsDialogOpen(false);
                    }}
                  >
                    <Pencil className="mr-2 h-4 w-4" /> Edit Employee
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
      {/* Escalation History Dialog */}
      <Dialog open={isEscHistoryDialogOpen} onOpenChange={setIsEscHistoryDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">
              Escalation History – {selectedEmployeeForHistory?.first_name}{' '}
              {selectedEmployeeForHistory?.last_name} ({selectedEmployeeForHistory?.code})
            </DialogTitle>
          </DialogHeader>
          {escHistLoading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin" />
            </div>
          ) : escalationHistory.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <History className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>No escalation history found</p>
            </div>
          ) : (
            <div className="rounded-md border overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Date & Time</th>
                    <th className="px-4 py-3 text-left font-medium">From</th>
                    <th className="px-4 py-3 text-left font-medium">To</th>
                    <th className="px-4 py-3 text-left font-medium">Type</th>
                    <th className="px-4 py-3 text-left font-medium">Item</th>
                    <th className="px-4 py-3 text-left font-medium">Level</th>
                    <th className="px-4 py-3 text-left font-medium">Reason</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {escalationHistory.map((log) => {
                    // const from = employees.find(e => e.id === log.escalated_from);
                    // const to = employees.find(e => e.id === log.escalated_to);
                    return (
                      <tr key={log.id} className="hover:bg-muted/50 transition-colors">
                        <td className="px-4 py-3">
                          {new Date(log.created_at).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {log.escalated_from ?? '-'}
                        </td>
                        <td className="px-4 py-3 font-medium text-primary">
                          {log.escalated_to ?? '-'}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={log.content_type === 'ticket' ? 'destructive' : 'secondary'}>
                            {log.content_type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 font-medium">
                          {log.object_repr}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <Badge variant="outline">Level {log.order}</Badge>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">
                          {log.reason || '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEscHistoryDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EmployeePage;